import { Label } from '@/shared/components/ui/label'
import { Checkbox } from '@/shared/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Input } from '@/shared/components/ui/input'
import type { Setting } from './generated-types'

function toSnakeCase(value: string): string {
  return value.replace(/[\s-]/g, '_').toLowerCase()
}

export function RenderSettings({
  setting,
  formContext,
  className,
  onChange,
}: {
  setting: Setting
  formContext?: {
    setFieldValue?: (field: string, value: unknown) => void
    values?: Record<string, unknown>
  }
  className?: string
  onChange?: (value: unknown) => void
}) {
  const fieldName = setting.key || toSnakeCase(setting.title)

  const titleDescription = (
    <div className="flex w-full flex-col gap-[2px]">
      <div className="text-[13px] font-medium text-[var(--ink)]">{setting.title}</div>
      <div className="text-[12px] text-[var(--ink-3)]">{setting.description}</div>
    </div>
  )

  const baseStyle = `border-b-[0.5px] border-[var(--rule)] pb-6 last:border-0 last:pb-0 ${className ?? ''}`
  const currentValue = formContext?.values?.[fieldName] ?? setting.value

  switch (setting.type) {
    case 'boolean':
    case 'check':
      return (
        <div className={`${baseStyle} flex items-center justify-between gap-4`} key={setting.title}>
          {titleDescription}
          <Checkbox
            checked={Boolean(currentValue)}
            onCheckedChange={(value) => {
              formContext?.setFieldValue?.(fieldName, value)
              setting.onChange?.(value)
              onChange?.(value)
            }}
          />
        </div>
      )

    case 'radio':
      return (
        <div className={`${baseStyle} flex items-center justify-between gap-4`} key={setting.title}>
          {titleDescription}
          <Checkbox
            checked={Boolean(currentValue)}
            onCheckedChange={(value) => {
              formContext?.setFieldValue?.(fieldName, value)
              setting.onChange?.(value)
              onChange?.(value)
            }}
          />
        </div>
      )

    case 'options':
      return (
        <div className={`${baseStyle} flex flex-col gap-2`} key={setting.title}>
          {titleDescription}
          <Select
            value={(currentValue as string) ?? ''}
            onValueChange={(value) => {
              formContext?.setFieldValue?.(fieldName, value)
              setting.onChange?.(value)
              onChange?.(value)
            }}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select an option" />
            </SelectTrigger>
            <SelectContent>
              {(setting.options ?? []).map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )

    case 'multiOptions':
      return (
        <div className={`${baseStyle} flex flex-col gap-3`} key={setting.title}>
          {titleDescription}
          <div className="flex flex-col gap-2 rounded-[8px] border-[0.5px] border-[var(--rule)] p-3">
            {(setting.options ?? []).map((option) => {
              const values = Array.isArray(currentValue) ? (currentValue as string[]) : []
              const isChecked = values.includes(option.value)
              return (
                <Label key={option.value} className="flex cursor-pointer items-center gap-2.5">
                  <Checkbox
                    checked={isChecked}
                    onCheckedChange={() => {
                      const next = isChecked
                        ? values.filter((value) => value !== option.value)
                        : [...values, option.value]
                      formContext?.setFieldValue?.(fieldName, next)
                      setting.onChange?.(next)
                      onChange?.(next)
                    }}
                  />
                  <span className="text-[13px] text-[var(--ink-2)]">{option.label}</span>
                </Label>
              )
            })}
          </div>
        </div>
      )

    case 'text':
      return (
        <div className={`${baseStyle} flex flex-col gap-2`} key={setting.title}>
          {titleDescription}
          <Input
            value={(currentValue as string) ?? ''}
            onChange={(event) => {
              formContext?.setFieldValue?.(fieldName, event.target.value)
              setting.onChange?.(event.target.value)
              onChange?.(event.target.value)
            }}
          />
        </div>
      )

    case 'custom':
      return (
        <div className={`${baseStyle} flex flex-col gap-2`} key={setting.title}>
          {titleDescription}
          {setting.content}
        </div>
      )
  }
}
